import type * as SentryType from '@sentry/react';

let sentryInstance: typeof SentryType | undefined;
let sentryReplayInstance:
  | ReturnType<typeof SentryType.replayIntegration>
  | undefined;

const parseSampleRate = (value: unknown): number => {
  const raw = Number(value);
  return Number.isFinite(raw) ? Math.min(Math.max(raw, 0), 1) : 0;
};

export const loadSentry = async () => {
  const Sentry = await import('@sentry/react');

  if (!sentryInstance) {
    sentryInstance = Sentry;
  }

  // Initialize the replay instance only once
  if (!sentryReplayInstance) {
    sentryReplayInstance = Sentry.replayIntegration({
      // disable all default masking
      // https://docs.sentry.io/platforms/javascript/session-replay/privacy/
      maskAllText: false,
      blockAllMedia: false,
      maskAllInputs: false,
      networkDetailAllowUrls: [
        new RegExp(
          import.meta.env.VITE_APP_API_BASE.replace(
            /^https?:\/\//,
            '^https?://'
          ).replace(/\//g, '\\/')
        ),
        new RegExp(
          import.meta.env.VITE_APP_ANALYTICS_URL.replace(
            /^https?:\/\//,
            '^https?://'
          ).replace(/\//g, '\\/')
        ),
      ],
    });
  }

  Sentry.init({
    environment:
      import.meta.env.VITE_APP_ENV ?? window.location.hostname.split('.')[1],
    dsn: import.meta.env.VITE_APP_SENTRY_DSN,
    release:
      import.meta.env.VITE_APP_ENV === 'prod'
        ? import.meta.env.VITE_APP_GIT_TAG ||
          import.meta.env.VITE_APP_GIT_BRANCH
        : 'develop',
    integrations: [
      // Tracing (performance monitoring - disabled for now)
      // https://docs.sentry.io/platforms/javascript/guides/react/tracing/
      // Sentry.browserTracingIntegration(),
      sentryReplayInstance,
    ],
    tracesSampleRate: parseSampleRate(
      import.meta.env.VITE_APP_SENTRY_TRACES_SAMPLE_RATE
    ),
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    // tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],

    // Session Replay
    // https://docs.sentry.io/platforms/javascript/guides/nextjs/session-replay/#sampling
    // disable session replay by default
    replaysSessionSampleRate: parseSampleRate(
      import.meta.env.VITE_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE
    ),
    replaysOnErrorSampleRate: parseSampleRate(
      import.meta.env.VITE_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE
    ),
    normalizeDepth: 12,
    beforeSend,
  });
};

// axios rejections that never got a response (offline, aborted, timed out,
// blocked). The message is stable but the minified culprit changes every
// build, so without a fixed fingerprint each release mints a new issue and
// re-fires the new-issue alert (FRAICHE-6B, FRAICHE-6D).
const isAxiosNetworkError = (error: unknown): error is Error =>
  error instanceof Error &&
  (error as { isAxiosError?: boolean }).isAxiosError === true &&
  !(error as { response?: unknown }).response;

export const beforeSend: NonNullable<
  SentryType.BrowserOptions['beforeSend']
> = (event, hint) => {
  // ignore playwright
  if (event.user?.email?.includes('mailslurp.com')) {
    return null;
  }
  const error = hint.originalException;
  // ignore OAuth refresh errors
  if (error instanceof Error && error.message === 'OAuth refresh error') {
    return null;
  }
  if (isAxiosNetworkError(error)) {
    event.fingerprint = [
      'axios-network-error',
      error.message.replace(/\d+/g, 'N'),
    ];
  }
  return event;
};

export const getSentryInstance = () => sentryInstance;

export const stopReplay = (enabled: boolean) => {
  const sentry = getSentryInstance();
  if (sentry && import.meta.env.VITE_APP_SENTRY_DSN && enabled) {
    const replay = sentry.getReplay();
    replay?.stop();
  }
};

// Ensure a continuous session replay is running. Three cases:
// - No replay id (fully stopped/expired via Sentry rate-limit or max replay
//   duration, or never sampled): start() a fresh session replay. This is the
//   recovery that closes the tender-endgame gap.
// - Buffer mode (replaysOnErrorSampleRate sampled this load into last-60s-on-
//   error buffering): getReplayId() is non-empty, but the session is NOT being
//   uploaded continuously. flush() promotes the buffer to a continuous session
//   (continueRecording defaults true), so a tender visitor who happened to land
//   in buffer mode still gets full coverage. Without this they would silently
//   no-op below and never record continuously - the gap Layer 1's drop to
//   replaysSessionSampleRate=0.1 makes reachable.
// - Already a running session replay: no-op (avoids the throw from start()).
// Limitation: getReplayId() still returns an id while a session is idle-PAUSED
// (~5min no DOM interaction), so this does not force-resume a paused session;
// Sentry auto-resumes that on the next user interaction.
export const ensureReplay = (enabled: boolean) => {
  const sentry = getSentryInstance();
  if (!enabled || !sentry || !import.meta.env.VITE_APP_SENTRY_DSN) return;
  const replay = sentry.getReplay();
  if (!replay) return;
  if (!replay.getReplayId()) {
    replay.start();
  } else if (replay.getRecordingMode() === 'buffer') {
    replay.flush();
  }
};
